import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTour } from '@reactour/tour';
import { getActiveTourKey } from './activeTour';
import { getTourConfig } from './tourRegistry';

// How long a step's target may stay gone before we give up on the tour.
const MISSING_GRACE_MS = 2500;

// Ends a running tour gracefully when the user goes off-script:
//   1. Navigates outside the tour's allowed routes (link, back button, a
//      click-through that changes the page).
//   2. Makes the current step's target disappear on the same page (closes the
//      drawer/modal it points at, dismisses a dialog, etc.) — only relevant for
//      click-through steps, since dimmed steps block stray clicks anyway.
// Closing routes through OnboardingTourController, which logs the outcome
// (skipped) and clears the active tour.
//
// Must be rendered inside the router (uses useLocation) and the TourProvider.
const TourGuard = () => {
	const location = useLocation();
	const { isOpen, currentStep, steps, setIsOpen } = useTour();

	// 1. Navigation guard.
	useEffect(() => {
		if (!isOpen) return;
		const key = getActiveTourKey();
		const allowed = key ? getTourConfig(key)?.allowedRoutes : undefined;
		if (!allowed || allowed.length === 0) return;

		const path = location.pathname;
		const onAllowedRoute = allowed.some(
			(r) => path === r || path.startsWith(`${r}/`),
		);
		if (!onAllowedRoute) setIsOpen(false);
	}, [location.pathname, isOpen, setIsOpen]);

	// 2. Target-vanished guard. Poll the current step's selector; once it has been
	// seen for this step, if it then stays gone past the grace window we end the
	// tour. Requiring it to appear first avoids closing before a step's target has
	// mounted. Centered/body steps always resolve, so they're exempt.
	useEffect(() => {
		if (!isOpen) return;
		const selector = steps?.[currentStep]?.selector;
		if (typeof selector !== 'string' || selector === 'body') return;

		let seen = false;
		let missingSince: number | null = null;
		const id = setInterval(() => {
			if (document.querySelector(selector)) {
				seen = true;
				missingSince = null;
				return;
			}
			if (!seen) return; // hasn't mounted yet — keep waiting
			if (missingSince == null) {
				missingSince = Date.now();
			} else if (Date.now() - missingSince > MISSING_GRACE_MS) {
				clearInterval(id);
				setIsOpen(false);
			}
		}, 400);
		return () => clearInterval(id);
	}, [isOpen, currentStep, steps, setIsOpen]);

	return null;
};

export default TourGuard;
